var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import S3ImageUploader from "@common-components/s3-image-uploader";
import { SIGNED_URL_UPLOAD_TYPE, useAnalytics, useError, useLoader, useNotification, useSignedURL, useUpdateSpace, uploadFileToPreSignedURL, } from "@piiqtechnologies/ui-lib";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import messages from "./messages.i18n";
import { useAuthContext } from "@stores/AuthContext";
import { RoleAliases } from "@constants/Roles";
const getFloorplanImageUrl = (key) => {
    if (!key)
        return key;
    if (key.startsWith("http://") || key.startsWith("https://")) {
        return key;
    }
    const mediaUrl = process.env.PEEK_APP_MEDIA_URL || "";
    return `${mediaUrl}floor-plans/${key}`;
};
const loadSvgAsDataUrl = (url) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const fullUrl = getFloorplanImageUrl(url);
        const response = yield fetch(fullUrl);
        if (!response.ok)
            return null;
        const svgText = yield response.text();
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
    }
    catch (error) {
        console.error("Error loading SVG as data URL:", error);
        return null;
    }
});
export const SpaceFloorplan = ({ floorplan, spaceId }) => {
    const intl = useIntl();
    const { setError } = useError();
    const { setLoader } = useLoader();
    const analytics = useAnalytics();
    const { setNotification } = useNotification();
    const [updateSpace, spaceUpdated, isUpdatingSpace, updateSpaceError] = useUpdateSpace();
    const [getImageUploadURL] = useSignedURL();
    const [editorImageUrl, setEditorImageUrl] = useState();
    const { proxyUser } = useAuthContext();
    useEffect(() => {
        setLoader(isUpdatingSpace);
        return () => setLoader(false);
    }, [isUpdatingSpace]);
    useEffect(() => {
        if (updateSpaceError)
            setError(updateSpaceError === null || updateSpaceError === void 0 ? void 0 : updateSpaceError.message);
    }, [updateSpaceError]);
    useEffect(() => {
        if (spaceUpdated) {
            setNotification(intl.formatMessage(messages.updateSpaceFloorplanSuccess));
            analytics.track("Floorplan Uploaded");
        }
    }, [spaceUpdated, intl, setNotification, analytics]);
    const onFloorplanChange = (imageKey) => __awaiter(void 0, void 0, void 0, function* () {
        updateSpace({
            _id: spaceId,
            floorPlan: { name: (floorplan === null || floorplan === void 0 ? void 0 : floorplan.name) || "", url: imageKey || "" },
        });
        const fullUrl = imageKey ? getFloorplanImageUrl(imageKey) : "";
        if (imageKey === null || imageKey === void 0 ? void 0 : imageKey.toLowerCase().endsWith(".svg")) {
            const dataUrl = yield loadSvgAsDataUrl(imageKey);
            setEditorImageUrl(dataUrl || fullUrl);
            if (!dataUrl && imageKey) {
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    const retryDataUrl = yield loadSvgAsDataUrl(imageKey);
                    if (retryDataUrl)
                        setEditorImageUrl(retryDataUrl);
                }), 1000);
            }
        }
        else {
            setEditorImageUrl(fullUrl || undefined);
        }
    });
    const handleEditorSave = (editedImageDataUrl) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            setLoader(true);
            if (!((_a = floorplan === null || floorplan === void 0 ? void 0 : floorplan.url) === null || _a === void 0 ? void 0 : _a.toLowerCase().endsWith(".svg"))) {
                setError("Edit floorplan is only available for SVG files");
                return;
            }
            if (!editedImageDataUrl.startsWith("data:image/svg+xml")) {
                throw new Error("Editor should only return SVG format");
            }
            const svgContent = decodeURIComponent(editedImageDataUrl.split(",").slice(1).join(","));
            const originalFileName = floorplan.url.split("/").pop() || "floorplan.svg";
            const baseName = originalFileName.replace(/\.svg$/i, "");
            const fileName = `${spaceId}/${baseName}-edited.svg`;
            const file = new File([new Blob([svgContent], { type: "image/svg+xml" })], fileName.split("/")[1], {
                type: "image/svg+xml",
            });
            const uploadURL = yield getImageUploadURL({
                uploadType: SIGNED_URL_UPLOAD_TYPE.FLOORPLAN,
                fileName,
            });
            yield uploadFileToPreSignedURL(uploadURL, file);
            setEditorImageUrl(editedImageDataUrl);
            updateSpace({
                _id: spaceId,
                floorPlan: { name: (floorplan === null || floorplan === void 0 ? void 0 : floorplan.name) || "", url: fileName },
            });
        }
        catch (ex) {
            setError((ex === null || ex === void 0 ? void 0 : ex.message) || "Error uploading edited image to s3");
            console.error("Error uploading edited image to s3", ex);
        }
        finally {
            setLoader(false);
        }
    });
    useEffect(() => {
        var _a;
        if ((_a = floorplan === null || floorplan === void 0 ? void 0 : floorplan.url) === null || _a === void 0 ? void 0 : _a.toLowerCase().endsWith(".svg")) {
            loadSvgAsDataUrl(floorplan.url).then((dataUrl) => {
                setEditorImageUrl(dataUrl || getFloorplanImageUrl(floorplan.url));
            });
        }
        else {
            setEditorImageUrl((floorplan === null || floorplan === void 0 ? void 0 : floorplan.url) ? getFloorplanImageUrl(floorplan.url) : undefined);
        }
    }, [floorplan === null || floorplan === void 0 ? void 0 : floorplan.url]);
    return (React.createElement(S3ImageUploader, { imageKey: spaceId, imageUrl: (floorplan === null || floorplan === void 0 ? void 0 : floorplan.url) ? getFloorplanImageUrl(floorplan.url) : "", label: intl.formatMessage(messages.spaceFloorplanUploadTitle), onChange: onFloorplanChange, bucket: SIGNED_URL_UPLOAD_TYPE.FLOORPLAN, enableEditor: (proxyUser === null || proxyUser === void 0 ? void 0 : proxyUser.roleAlias) === RoleAliases.ADMIN, onEditorSave: handleEditorSave, editorImageUrl: editorImageUrl }));
};
export default SpaceFloorplan;
